import { alphabeticalSort } from '../alphabeticalSort'
import { ComplexEnumItem } from './ComplexEnumItem'

export abstract class ComplexEnum<
  Typename extends string,
  ItemType extends ComplexEnumItem<ID>,
  ID extends string
> {
  /**
   * the internal name of the complex enum
   */
  abstract readonly typename: Typename
  /**
   * all {@link ItemType} properties organized as a list
   */
  abstract readonly values: ItemType[]

  isInstance = (item: unknown): item is ItemType => {
    return item instanceof ComplexEnumItem
  }

  /**
   * we were having issues with `this` being undefined in the browser so
   * all these methods are written as arrow functions to make sure `this` is
   * correctly bound
   */

  /**
   * throws early if the supplied {@link item} is undefined, enforcing the item's
   * existence
   *
   * @example
   * const message = `No ${this.typename} could be found with value: ${value}`
   * const noValueMessage = `No ${this.typename} could be found`
   *
   * @throws a new error using the supplied arguments
   * @protected not intended for use outside {@link ComplexEnum} or its inherited classes
   */
  protected orFail = <T, U = ItemType>(
    item: U | undefined,
    value: T | undefined
  ) => {
    if (!item)
      throw new Error(
        `No ${this.typename} could be found` +
          (value ? ` with value: ${value}` : '')
      )
    return item
  }
  /**
   * {@link values} mapped to their id
   */
  get ids() {
    return this.map(({ id }) => id)
  }
  /**
   * coerces the supplied ids into their hard-typed counterparts
   *
   * @param ids a list of strings to coerce
   * @throws if no value can be found with any id
   */
  toIds = (ids: (string | null | undefined)[]) =>
    ids.map((id) => this.fromIdOrFail(id).id)
  /**
   * attempts to find the first value where the {@link id} matches the
   * item's {@link ComplexEnumItem.id|id}
   *
   * @param id the id to search with
   */
  fromId = (id: string | null | undefined) =>
    this.find((value) => value.id === id)
  /**
   * finds the first value where the {@link id} matches the item's
   * {@link ComplexEnumItem.id|id}
   *
   * @param id the id to search with
   * @throws if no value can be found with the id
   * @see {@link fromId}
   */
  fromIdOrFail = (id: string | null | undefined) =>
    this.orFail(this.fromId(id), id)
  /**
   * attempts to transform the supplied {@link ids} into {@link ItemType|items}
   *
   * @param ids the ids to map
   * @see {@link fromId}
   */
  fromIds = (ids: (string | null | undefined)[]) => ids.map(this.fromId)
  /**
   * transforms the supplied {@link ids} into {@link ItemType|items}
   *
   * @param ids the ids to map
   * @see {@link fromIdOrFail}
   * @throws if any values cannot be found with any id
   */
  fromIdsOrFail = (ids: (string | null | undefined)[]) =>
    ids.map(this.fromIdOrFail)
  /**
   * transforms the supplied {@link ids} into {@link ItemType|items}
   *
   * if the item can't be found, it will be ignored
   *
   * this is useful if you don't want to throw an error and can still do something
   * useful with a partial list
   *
   * @param ids the ids to filter
   * @see {@link fromId}
   */
  fromIdOrFilter = (ids: (string | null | undefined)[]) =>
    ids.reduce((items, id) => {
      const found = this.fromId(id)
      if (found) items.push(found)
      return items
    }, Array<ItemType>())
  /**
   * attemps to find the first {@link ItemType} with the supplied {@link predicate}
   */
  find = (predicate: (value: ItemType) => boolean) =>
    this.values.find(predicate)
  /**
   * finds the first {@link ItemType} with the supplied {@link predicate}
   *
   * @throws if no item can be found
   */
  findOrFail = (predicate: (value: ItemType) => boolean) =>
    this.orFail(this.find(predicate), undefined)
  /**
   * filters {@link values} based on the supplied {@link predicate}
   *
   * @param predicate a callback to execute on {@link values}
   */
  filter = (predicate: (value: ItemType) => boolean) =>
    this.values.filter(predicate)
  /**
   * maps {@link values} into new values using the supplied {@link transformer}
   *
   * @param transformer a transformer callback to execute on every {@link ItemType}
   */
  map = <T>(transformer: (value: ItemType) => T) => this.values.map(transformer)
  /**
   * checks to see if some {@link ItemType} values conform to the supplied {@link predicate}
   *
   * @param predicate a callback to execute on {@link values}
   */
  some = (predicate: (value: ItemType) => boolean) =>
    this.values.some(predicate)
  /**
   * checks to see if every {@link ItemType} conforms to the supplied {@link predicate}
   *
   * @param predicate a callback to execute on every {@link ItemType}
   */
  every = (predicate: (value: ItemType) => boolean) =>
    this.values.every(predicate)
  /**
   * reduces {@link values} into a new value using the supplied {@link reducer} and {@link initialValue}
   *
   * @param reducer a callback to execute using the previous and current values
   * @param initialValue the initial value to start the reducer from
   */
  reduce = <T>(
    reducer: (previous: T, current: ItemType) => T,
    initialValue: T
  ) => this.values.reduce(reducer, initialValue)
  /**
   * sorts {@link values} into a new array using the supplied {@link compareFn}
   *
   * @param compareFn a callback to determine the sort order. sorts by id by default
   */
  sort = (compareFn?: (a: ItemType, b: ItemType) => number) => {
    const values = Array.from(this.values).sort(
      compareFn ? compareFn : alphabeticalSort('id')
    )
    return values
  }
}
